import * as yup from 'yup'
import { DiscountCodeBEForm, DiscountCodeDetails } from 'discounts/types'
import { parseErrorsFromBE } from 'utils/errorHandler'
import cleanDeep from 'clean-deep'
import { parseISODate } from 'utils/dates'
import { format } from 'date-fns'

export type DiscountType = 'cart' | 'product'

export interface DiscountCodeFormSchema {
    poolUuid?: string
    id?: string
    type: DiscountType
    code: string
    bookingWindow: {
        from: string
        to: string
    }
    eventWindow: {
        from: string
        to: string
    } | null
    enableEventWindow: boolean
    products?: {
        uuids?: string[]
        amount?: number
    }
    enableProductAmount: boolean
    rate: 'flat' | 'percentage'
    reductionRate: string
    maxUses?: number
    enableMaxUses: boolean
    remainderUsable?: boolean
}

export const discountCodeFormatter = {
    toForm(discount?: DiscountCodeDetails): DiscountCodeFormSchema {
        if (!discount) {
            return {} as any
        }
        const products = discount.products as any
        return {
            poolUuid: discount.discountPoolUuid,
            id: discount.id,
            code: discount.code,
            type: products?.length > 0 ? 'product' : 'cart',
            bookingWindow: {
                from: discount.bookingWindow?.fromDate,
                to: discount.bookingWindow?.toDate,
            },
            eventWindow: discount.eventWindow
                ? {
                      from: discount.eventWindow.fromDate,
                      to: discount.eventWindow.toDate,
                  }
                : null,
            enableEventWindow: !!discount.eventWindow,
            products: {
                amount: discount.productsAmount !== 10001 ? discount.productsAmount : undefined,
                uuids: discount?.products?.map((product) => product.uuid),
            },
            enableProductAmount: !!discount.productsAmount && discount.productsAmount !== 10001,
            enableMaxUses: !!discount.maxUses,
            maxUses: discount.maxUses,
            remainderUsable: discount.remainderUsable,
            rate: discount.discount?.type,
            reductionRate: discount.discount?.amount,
        }
    },
    toDomain(discount: DiscountCodeFormSchema): DiscountCodeBEForm {
        return cleanDeep(
            {
                discountPoolUuid: discount.poolUuid,
                id: discount.id,
                code: discount.code,
                discount: {
                    amount: discount.reductionRate,
                    type: discount.rate,
                },
                remainderUsable: discount.remainderUsable,
                maxUses: discount.enableMaxUses ? discount.maxUses : undefined,
                products: {
                    uuids: discount.products?.uuids || [],
                    amount: discount.enableProductAmount ? discount.products?.amount : 10001, // TODO: Remove after limit is removed in backend
                },
                bookingWindow: {
                    fromDate: format(parseISODate(discount?.bookingWindow?.from), 'yyyy-MM-dd'),
                    toDate: format(parseISODate(discount?.bookingWindow?.to), 'yyyy-MM-dd'),
                },
                eventWindow: discount.enableEventWindow
                    ? {
                          fromDate: discount?.eventWindow?.from
                              ? format(parseISODate(discount?.eventWindow.from), 'yyyy-MM-dd')
                              : undefined,
                          toDate: discount?.eventWindow?.to
                              ? format(parseISODate(discount?.eventWindow.to), 'yyyy-MM-dd')
                              : undefined,
                      }
                    : undefined,
            },
            { emptyArrays: false },
        ) as DiscountCodeBEForm
    },
    formatErrors(errors: any): any {
        const parsedErrors = parseErrorsFromBE(errors)

        return cleanDeep({
            code: parsedErrors.code,
            bookingWindow: {
                from: parsedErrors.validFrom,
                to: parsedErrors.validTo,
            },
            products: parsedErrors.products,
            maxUses: parsedErrors.maxUses,
            remainderUsable: parsedErrors.remainderUsable,
            reductionRate: parsedErrors.percentageReduction || parsedErrors.flatReduction,
        })
    },
}

export const discountCodeSchema = yup.object().shape({
    code: yup.string().required(),
    bookingWindow: yup.object().required(),
    reductionRate: yup
        .number()
        .min(1)
        .required()
        .when('rate', {
            is: 'percentage',
            then: yup.number().max(100),
        }),
    maxUses: yup.number().when('enableMaxUses', {
        is: true,
        then: yup.number().min(1).required(),
        otherwise: yup.number().nullable(),
    }),
    products: yup.object().when('type', {
        is: 'product',
        then: yup.object().shape({
            uuids: yup.array().min(1).required(),
            amount: yup.number().min(1).nullable(),
        }),
        otherwise: yup.object().nullable(),
    }),
    poolUuid: yup.string().nullable().required(),
    remainderUsable: yup
        .boolean()
        .when(['enableMaxUses', 'maxUses'], {
            is: (enableMaxUses: boolean, maxUses: number) => !enableMaxUses || maxUses !== 1,
            then: yup.boolean().isFalse('Can only be enabled if "Limit total uses" is set to 1'),
        })
        .when('rate', {
            is: 'percentage',
            then: yup.boolean().isFalse('Can only be enabled if "Discount value" is set to "Fixed amount"'),
        }),
})
